import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { UploadFileOutlined as ImportIcon } from '@mui/icons-material';
import VehicleService, {
  OwnershipKind, Vehicle, VehicleInput, VehicleOwnership, VehicleStatus,
} from '../../api/services/vehicle.service';
import { useAuthStore } from '../../store/authStore';

// Add or edit one truck.
//
// Unit number is the only required field, and deliberately so: a van comes
// back from the yard with a number on the door and nothing else known, and
// a form that demands a VIN before it will save means the truck never gets
// entered at all.
//
// Three groups, the way the owner's vehicle sheet reads: the truck, its
// registration, and what it costs. The cost group is AO/CA only — the API
// neither sends nor accepts it for anyone else, so hiding it here is the
// courtesy, not the gate.

interface Props {
  open: boolean;
  vehicle?: Vehicle | null;
  onClose: () => void;
  onSaved: (vehicle: Vehicle) => void;
  /** Importing is another way to add trucks, so it is offered from here. */
  onImport?: () => void;
}

type Form = Record<string, string | boolean>;

const TEXT_FIELDS = [
  'unitNumber', 'vin', 'year', 'make', 'model', 'bodyType', 'fuel',
  'licensePlate', 'plateState', 'serviceAreaId', 'titleNumber',
  'registrationExpiresOn', 'insuranceRenewsOn', 'notes',
  'propertyTaxAccount', 'propertyTaxAmount',
] as const;
const OWNERSHIP_FIELDS = [
  'kind', 'lienholder', 'startedOn', 'maturesOn', 'termMonths', 'invoiceValue',
  'amountFinanced', 'taxFinanced', 'downPayment', 'monthlyPayment', 'totalCost',
  'interestRate', 'notes',
] as const;
const DATE_KEYS = new Set(['registrationExpiresOn', 'insuranceRenewsOn', 'own.startedOn', 'own.maturesOn']);
const NUMBER_KEYS = new Set([
  'year', 'serviceAreaId', 'propertyTaxAmount', 'own.termMonths', 'own.invoiceValue',
  'own.amountFinanced', 'own.taxFinanced', 'own.downPayment', 'own.monthlyPayment',
  'own.totalCost', 'own.interestRate',
]);

const STATUSES: { value: VehicleStatus; label: string }[] = [
  { value: 'active', label: 'On the road' },
  { value: 'maintenance', label: 'In maintenance' },
];
const KINDS: { value: OwnershipKind; label: string }[] = [
  { value: 'cash', label: 'Owned (cash)' },
  { value: 'financed', label: 'Financed' },
  { value: 'leased', label: 'Leased' },
];

const dateInput = (iso?: string | null) => (iso ? String(iso).slice(0, 10) : '');

function toForm(vehicle?: Vehicle | null): Form {
  const form: Form = { status: 'active' };
  if (!vehicle) return { ...form, unitNumber: '' };
  for (const key of TEXT_FIELDS) {
    const value = (vehicle as any)[key];
    form[key] = DATE_KEYS.has(key) ? dateInput(value) : value == null ? '' : String(value);
  }
  const own: VehicleOwnership = vehicle.ownership || {};
  for (const key of OWNERSHIP_FIELDS) {
    const value = (own as any)[key];
    form[`own.${key}`] = DATE_KEYS.has(`own.${key}`) ? dateInput(value) : value == null ? '' : String(value);
  }
  form.status = vehicle.status === 'retired' ? 'active' : vehicle.status;
  form.registrationReceived = !!vehicle.registrationReceived;
  form.onScheduleB = !!vehicle.onScheduleB;
  return form;
}

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.8, display: 'block', mb: 1 }}
    >
      {title}
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
      {children}
    </Box>
  </Box>
);

const VehicleDialog: React.FC<Props> = ({ open, vehicle, onClose, onSaved, onImport }) => {
  const { canArea } = useAuthStore();
  // Seeing what a truck costs is `fleetCost`; the Cost group is editable with edit.
  const canSeeMoney = canArea('fleetCost', 'edit');

  const [form, setForm] = useState<Form>(toForm(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vinFilled, setVinFilled] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setVinFilled(false);
    setForm(toForm(vehicle));
  }, [open, vehicle]);

  const val = (key: string) => String(form[key] ?? '');
  const set = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  // The VIN already says what the truck is. Fills only what is still
  // empty, so it never overwrites something somebody typed.
  const lookUpVin = async () => {
    const vin = val('vin').trim();
    if (vin.length !== 17) return;
    const decoded = await VehicleService.decodeVin(vin);
    let filled = false;
    setForm((prev) => {
      const next = { ...prev };
      (['year', 'make', 'model', 'fuel'] as const).forEach((key) => {
        if (decoded[key] != null && !String(prev[key] ?? '').trim()) {
          next[key] = String(decoded[key]);
          filled = true;
        }
      });
      return next;
    });
    setVinFilled(filled);
  };

  const read = (key: string, editing: boolean) => {
    const raw = String(form[key] ?? '').trim();
    // On a new truck a blank is "not known"; on an edit it clears.
    if (!raw) return editing ? null : undefined;
    return NUMBER_KEYS.has(key) ? Number(raw) : raw;
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const editing = !!vehicle;
      const payload: VehicleInput = { unitNumber: val('unitNumber').trim() };
      for (const key of TEXT_FIELDS) {
        if (key === 'unitNumber') continue;
        if (!canSeeMoney && key.startsWith('propertyTax')) continue;
        const value = read(key, editing);
        if (value !== undefined) (payload as any)[key] = value;
      }
      payload.registrationReceived = !!form.registrationReceived;
      payload.onScheduleB = !!form.onScheduleB;
      payload.status = form.status as VehicleStatus;
      if (canSeeMoney) {
        const own: Record<string, unknown> = {};
        for (const key of OWNERSHIP_FIELDS) {
          const value = read(`own.${key}`, editing);
          if (value !== undefined) own[key] = value;
        }
        if (Object.keys(own).length) payload.ownership = own;
      }

      const saved = vehicle
        ? await VehicleService.update(vehicle._id, payload)
        : await VehicleService.create(payload);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that vehicle');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: string, label: string, span: number, extra: Record<string, any> = {}) => (
    <TextField
      label={label}
      value={val(key)}
      onChange={set(key)}
      size="small"
      fullWidth
      sx={{ gridColumn: { xs: 'auto', sm: `span ${span * 2}` } }}
      {...(DATE_KEYS.has(key) ? { type: 'date', InputLabelProps: { shrink: true } } : {})}
      {...extra}
    />
  );
  const money = (key: string, label: string, span = 2) => field(key, label, span, {
    inputMode: 'decimal',
    InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
  });

  const kind = val('own.kind') as OwnershipKind | '';
  const onPaper = kind === 'financed' || kind === 'leased';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{vehicle ? `Edit ${vehicle.unitNumber}` : 'Add a vehicle'}</DialogTitle>
      <DialogContent>
        <Stack gap={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Group title="Vehicle">
            {field('unitNumber', 'Unit number', 2, { required: true, autoFocus: !vehicle })}
            {field('vin', 'VIN', 4, {
              onBlur: lookUpVin,
              inputProps: { style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }, maxLength: 17 },
              helperText: vinFilled ? 'Year, make and model filled from the VIN' : undefined,
            })}
            {field('year', 'Year', 1, { inputMode: 'numeric' })}
            {field('make', 'Make', 2)}
            {field('model', 'Model', 3)}
            {field('bodyType', 'Body type', 2, { placeholder: 'WALK IN P1000' })}
            {field('fuel', 'Fuel', 1)}
            {field('serviceAreaId', 'Service area', 1, { inputMode: 'numeric' })}
            {field('status', 'Status', 2, {
              select: true,
              children: STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>),
            })}
          </Group>

          <Group title="Registration">
            {field('licensePlate', 'Plate', 2)}
            {field('plateState', 'State', 1, { inputProps: { maxLength: 2 } })}
            {field('titleNumber', 'Title number', 3)}
            {field('registrationExpiresOn', 'Registration expires', 2)}
            {field('insuranceRenewsOn', 'Insurance renews', 2)}
            <Stack direction="row" gap={1} sx={{ gridColumn: { xs: 'auto', sm: 'span 4' } }} alignItems="center">
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small" checked={!!form.registrationReceived}
                    onChange={(e) => setForm((p) => ({ ...p, registrationReceived: e.target.checked }))}
                  />
                )}
                label={<Typography variant="body2">Received</Typography>}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small" checked={!!form.onScheduleB}
                    onChange={(e) => setForm((p) => ({ ...p, onScheduleB: e.target.checked }))}
                  />
                )}
                label={<Typography variant="body2">Schedule B</Typography>}
              />
            </Stack>
          </Group>

          {canSeeMoney && (
            <Group title="Cost">
              {field('own.kind', 'Ownership', 2, {
                select: true,
                children: [
                  <MenuItem key="" value=""><em>Not set</em></MenuItem>,
                  ...KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>),
                ],
              })}
              {money('own.invoiceValue', kind === 'cash' ? 'Purchase price' : 'Invoice value')}
              {field('propertyTaxAccount', 'Property tax #', 1)}
              {money('propertyTaxAmount', 'Property tax', 1)}
              {onPaper && (
                <>
                  {field('own.lienholder', kind === 'leased' ? 'Lessor' : 'Lienholder', 3)}
                  {field('own.startedOn', 'Start', 1.5)}
                  {field('own.maturesOn', 'Matures', 1.5)}
                  {field('own.termMonths', 'Term (months)', 1, { inputMode: 'numeric' })}
                  {field('own.interestRate', 'Rate', 1, {
                    inputMode: 'decimal',
                    InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                  })}
                  {money('own.monthlyPayment', 'Monthly payment')}
                  {money('own.downPayment', 'Down payment')}
                  {money('own.amountFinanced', 'Financed', 2)}
                  {money('own.taxFinanced', 'Tax financed', 2)}
                  {money('own.totalCost', 'Total cost', 2)}
                </>
              )}
              {field('own.notes', 'Financing notes', 6)}
            </Group>
          )}

          {field('notes', 'Notes', 6, { multiline: true, minRows: 2 })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!vehicle && onImport && (
          <Button startIcon={<ImportIcon />} onClick={onImport} sx={{ mr: 'auto' }}>
            Import a spreadsheet
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={saving || !val('unitNumber').trim()}>
          {saving ? 'Saving…' : vehicle ? 'Save changes' : 'Add vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleDialog;
